
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 4,
  layout: 'reference',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for endpoints and webhooks associated with the Plaid Identity product.',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Identity' subtitle='API reference for Identity endpoints and webhooks' compact mdxType="Header" />
    <p>{`Verify the name, address, phone number, and email address of a user against bank account information on file. For how-to guidance, see the `}<a parentName="p" {...{
        "href": "/docs/identity/"
      }}>{`Identity documentation`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/identity/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fetch identity data`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/identity/match`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Match client identity with bank identity`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/identity/match/list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List Link Identity Match attempts`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/identity/documents/uploads/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get Identity data parsed from an uploaded bank statement`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "endpoints"
    }}>{`Endpoints`}</h3>
    <h4 {...{
      "id": "identityget"
    }}>{`/identity/get`}</h4>
    <Schema method='POST' route='/identity/get' type='request' mdxType="Schema">
      <MDXCodeBlock title='/identity/get' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`# pull identity data for an item
request = Plaid::IdentityGetRequest.new({ access_token: access_token })
response = client.identity_get(request)
accounts = response.accounts
identities = accounts.map { |account| account.owners }.flatten
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/identity/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": String
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`// Pull Identity data for an Item
const request: IdentityGetRequest = {
  access_token: accessToken,
};
try {
  const response = await plaidClient.identityGet(request);
  const identities = response.data.accounts.flatMap(
    (account) => account.owners,
  );
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`// Pull Identity data for an Item
IdentityGetRequest request = new IdentityGetRequest()
  .accessToken(accessToken);
Response<IdentityGetResponse> response = client().identityGet(request).execute();

List<AccountIdentity> accounts = response.body().getAccounts();

// Java 7
List<Owner> identities = new ArrayList<>();
for (AccountIdentity account : accounts) {
  identities.addAll(account.getOwners());
}

// Java 8 and above
List<Owner> identitiesJava8 = accounts.stream()
  .flatMap(account -> account.getOwners().stream())
  .collect(Collectors.toList());
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`# pull identity data for an item
request = IdentityGetRequest(access_token=access_token)
response = client.identity_get(request)
accounts = response['accounts']
identities = []
for account in accounts:
    identities.extend(account['owners'])
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewIdentityGetRequest(accessToken)
response, _, err := client.PlaidApi.IdentityGet(ctx).IdentityGetRequest(
  *request,
).Execute()

accounts := response.GetAccounts()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.IdentityGetRequest{AccessToken: accessToken}
response, err := client.Identity.Get(ctx, request)

accounts := response.GetAccounts()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/identity/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "identitymatch"
    }}>{`/identity/match`}</h4>
    <Schema method='POST' route='/identity/match' type='request' mdxType="Schema">
      <MDXCodeBlock title='/identity/match' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`# Omit user object if using identity verification / financial account matching
user = Plaid::IdentityMatchUser.new(
  legal_name: 'Jane Smith',
  phone_number: '+14155552671',
  email_address: 'jane.smith@example.com',
  address: Plaid::AddressDataNullableNoRequiredFields.new(
    street: '500 Market St',
    city: 'San Francisco',
    region: 'CA',
    postal_code: '94105',
    country: 'US'
  )
)

request = Plaid::IdentityMatchRequest.new(
  access_token: access_token,
  user: user
)

response = client.identity_match(request)
accounts = response.accounts

accounts.map do |account|
  name_score = account.legal_name&.score
  phone_score = account.phone_number&.score
  email_score = account.email_address&.score
  address_score = account.address&.score
end
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`# Omit user object if using Identity Verification / Financial Account Matching
curl -X POST https://sandbox.plaid.com/identity/match \\
-H 'Content-Type: application/json' \\
-d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "access_token": String,
   "user": {
       "phone_number": String,
       "legal_name": String,
       "email_address": String,
       "address": {
           "street": String,
           "region": String,
           "country": String,
           "city": String,
           "postal_code": String
       }
    }
}'

`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  access_token: accessToken,
  // Omit user object if using Identity Verification Financial Account Matching
  user: {
    legal_name: 'Jane Smith',
    phone_number: '+14155552671',
    email_address: 'jane.smith@example.com',
    address: {
      street: '500 Market St',
      city: 'San Francisco',
      region: 'CA',
      postal_code: '94105',
      country: 'US',
    },
  },
};

try {
  const response = await plaidClient.identityMatch(request);
  const accounts = response.data.accounts;
  for (const account of accounts) {
    const legalNameScore = account.legal_name?.score;
    const phoneScore = account.phone_number?.score;
    const emailScore = account.email_address?.score;
    const addressScore = account.address?.score;
  }
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`// Omit user object if using Identity Verification / Financial Account Matching
IdentityMatchUser user = new IdentityMatchUser()
    .legalName("Jane Smith")
    .phoneNumber("+14155552671")
    .emailAddress("jane.smith@example.com")
    .address(new AddressDataNullableNoRequiredFields()
        .street("500 Market St")
        .city("San Francisco")
        .region("CA")
        .postalCode("94105")
        .country("US"));

IdentityMatchRequest request = new IdentityMatchRequest()
    .accessToken(accessToken)
    .user(user);

Response<IdentityMatchResponse> response = plaidClient.identityMatch(request).execute();

List<AccountIdentityMatchScore> accounts = response.body().getAccounts();

for (AccountIdentityMatchScore account : accounts) {
  Integer legalNameScore = account.getLegalName() != null ? account.getLegalName().getScore() : null;
  Integer phoneNumberScore = account.getPhoneNumber() != null ? account.getPhoneNumber().getScore() : null;
  Integer emailAddressScore = account.getEmailAddress() != null ? account.getEmailAddress().getScore() : null;
  Integer addressScore = account.getAddress() != null ? account.getAddress().getScore() : null;

  // Compare scores to risk thresholds
}

`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`# Omit user object if using Identity Verification / Financial Account Matching
user = IdentityMatchUser(
    legal_name="Jane Smith",
    phone_number="+14155552671",
    email_address="jane.smith@example.com",
    address=AddressDataNullableNoRequiredFields(
        street="500 Market St",
        city="San Francisco",
        region="CA",
        postal_code="94105",
        country="US"
    )
)

request = IdentityMatchRequest(
    access_token=access_token,
    user=user
)

response = client.identity_match(request)
accounts = response['accounts']

for account in accounts:
    name_score = account['legal_name']['score'] if account['legal_name'] is not None else None
    phone_score = account['phone_number']['score'] if account['phone_number'] is not None else None
    email_score = account['email_address']['score'] if account['email_address'] is not None else None
    address_score = account['address']['score'] if account['address'] is not None else None
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`
// Omit user object if using Identity Verification / Financial Account Matching
user := plaid.IdentityMatchUser{}
user.SetLegalName("Jane Smith")
user.SetPhoneNumber("+14155552671")
user.SetEmailAddress("jane.smith@example.com")

address := plaid.AddressDataNullableNoRequiredFields{}
address.SetStreet("500 Market St")
address.SetCity("San Francisco")
address.SetRegion("CA")
address.SetPostalCode("94105")
address.SetCountry("US")
user.SetAddress(address)

request := plaid.NewIdentityMatchRequest(accessToken)
request.SetUser(user)

response, _, err := client.PlaidApi.IdentityMatch(ctx).IdentityMatchRequest(*request).Execute()
if err != nil {
    log.Fatal(err)
}

accounts := response.GetAccounts()
for _, account := range accounts {
    if legalName, ok := account.GetLegalNameOk(); ok {
        legalNameScore := legalName.GetScore()
        _ = legalNameScore
    }
    if phoneNumber, ok := account.GetPhoneNumberOk(); ok {
        phoneScore := phoneNumber.GetScore()
        _ = phoneScore
    }
    if emailAddress, ok := account.GetEmailAddressOk(); ok {
        emailScore := emailAddress.GetScore()
        _ = emailScore
    }
    if address, ok := account.GetAddressOk(); ok {
        addressScore := address.GetScore()
        _ = addressScore
    }
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`// Omit user object if using Identity Verification / Financial Account Matching
user := plaid.IdentityMatchUser{}
user.LegalName = plaid.String("Jane Smith")
user.PhoneNumber = plaid.String("+14155552671")
user.EmailAddress = plaid.String("jane.smith@example.com")

address := plaid.AddressDataNullableNoRequiredFields{}
address.Street = plaid.String("500 Market St")
address.City = plaid.String("San Francisco")
address.Region = plaid.String("CA")
address.PostalCode = plaid.String("94105")
address.Country = plaid.String("US")
user.Address = &address

request := &plaid.IdentityMatchRequest{AccessToken: accessToken}
request.User = &user

response, err := client.Identity.Match(ctx, request)
if err != nil {
    log.Fatal(err)
}

accounts := response.GetAccounts()
for _, account := range accounts {
    if legalName := account.GetLegalName(); legalName != nil {
        legalNameScore := legalName.GetScore()
        _ = legalNameScore
    }
    if phoneNumber := account.GetPhoneNumber(); phoneNumber != nil {
        phoneScore := phoneNumber.GetScore()
        _ = phoneScore
    }
    if emailAddress := account.GetEmailAddress(); emailAddress != nil {
        emailScore := emailAddress.GetScore()
        _ = emailScore
    }
    if address := account.GetAddress(); address != nil {
        addressScore := address.GetScore()
        _ = addressScore
    }
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/identity/match' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "identitymatchlist"
    }}>{`/identity/match/list`}</h4>
    <Schema method='POST' route='/identity/match/list' type='request' mdxType="Schema">
      <MDXCodeBlock title='/identity/match/list' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: IdentityMatchListRequest = {
  start_time: '2026-07-12T00:00:00Z',
  end_time: '2026-07-13T01:42:00Z',
  count: 25,
};

try {
  const response = await plaidClient.identityMatchList(request);
  const attempts = response.data.identity_match_attempts;
  const nextCursor = response.data.next_cursor;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/identity/match/list \\
-H 'Content-Type: application/json' \\
-d @- <<EOF
{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "start_time": "2026-07-12T00:00:00Z",
  "end_time": "2026-07-13T01:42:00Z",
  "count": 25
}
EOF
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::IdentityMatchListRequest.new(
  {
    start_time: '2026-07-12T00:00:00Z',
    end_time: '2026-07-13T01:42:00Z',
    count: 25
  }
)
response = client.identity_match_list(request)
attempts = response.identity_match_attempts
next_cursor = response.next_cursor
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`IdentityMatchListRequest request = new IdentityMatchListRequest()
  .startTime(OffsetDateTime.parse("2026-07-12T00:00:00Z"))
  .endTime(OffsetDateTime.parse("2026-07-13T01:42:00Z"))
  .count(25);
Response<IdentityMatchListResponse> response = client()
  .identityMatchList(request)
  .execute();
List<IdentityMatchListAttempt> attempts =
  response.body().getIdentityMatchAttempts();
String nextCursor = response.body().getNextCursor();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = IdentityMatchListRequest(
    start_time=datetime.datetime(2026, 7, 12, tzinfo=datetime.timezone.utc),
    end_time=datetime.datetime(2026, 7, 13, 1, 42, tzinfo=datetime.timezone.utc),
    count=25
)
response = client.identity_match_list(request)
attempts = response['identity_match_attempts']
next_cursor = response['next_cursor']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`startTime := time.Date(2026, time.July, 12, 0, 0, 0, 0, time.UTC)
endTime := time.Date(2026, time.July, 13, 1, 42, 0, 0, time.UTC)
request := plaid.NewIdentityMatchListRequest(startTime, endTime)
request.SetCount(25)

response, _, err := client.PlaidApi.
  IdentityMatchList(ctx).
  IdentityMatchListRequest(*request).
  Execute()
attempts := response.GetIdentityMatchAttempts()
nextCursor := response.GetNextCursor()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`startTime := time.Date(2026, time.July, 12, 0, 0, 0, 0, time.UTC)
endTime := time.Date(2026, time.July, 13, 1, 42, 0, 0, time.UTC)
request := &plaid.IdentityMatchListRequest{
  StartTime: startTime,
  EndTime: endTime,
  Count: plaid.Int(25),
}

page, err := client.Identity.ListMatches(ctx, request)
if err != nil {
  log.Fatal(err)
}
response := page.Response
attempts := response.GetIdentityMatchAttempts()
nextCursor := response.GetNextCursor()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/identity/match/list' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "identitydocumentsuploadsget"
    }}>{`/identity/documents/uploads/get`}</h4>
    <Schema method='POST' route='/identity/documents/uploads/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/identity/documents/uploads/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: IdentityDocumentsUploadsGetRequest = {
  access_token: accessToken,
};
try {
  const response = await client.identityDocumentsUploadsGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/identity/documents/uploads/get \\
-H 'Content-Type: application/json' \\
-d '{
  "access_token": String,
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::IdentityDocumentsUploadsGetRequest.new(
  {
    access_token: access_token
  }
)
response = client.identity_documents_uploads_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`
IdentityDocumentsUploadsGetRequest request = new IdentityDocumentsUploadsGetRequest()
  .accessToken(accessToken);

Response<IdentityDocumentsUploadsGetResponse> response = client()
  .identityDocumentsUploadsGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = IdentityDocumentsUploadsGetRequest(
    access_token=access_token
)
response = client.identity_documents_uploads_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewIdentityDocumentsUploadsGetRequest(
  accessToken,
)

response, _, err := client.PlaidApi.
  IdentityDocumentsUploadsGet(ctx).
  IdentityDocumentsUploadsGetRequest(*request).
  Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.IdentityDocumentsUploadsGetRequest{AccessToken: accessToken}

response, err := client.Identity.GetDocumentUploads(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/identity/documents/uploads/get' type='response' mdxType="Schema" />
    <h3 {...{
      "id": "webhooks-beta"
    }}>{`Webhooks (beta)`}</h3>
    <Callout mdxType="Callout">
      <p>{`This feature is currently in beta; please reach out to your Plaid account manager if you would like it enabled.`}</p>
    </Callout>
    <h4 {...{
      "id": "default_update"
    }}><inlineCode parentName="h4">{`DEFAULT_UPDATE`}</inlineCode></h4>
    <Schema model='IdentityDefaultUpdateWebhook' type='model' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "endpoints",
        level: 3,
        title: "Endpoints",
      },
{
        id: "identityget",
        level: 4,
        title: "/identity/get",
      },
{
        id: "identitymatch",
        level: 4,
        title: "/identity/match",
      },
{
        id: "identitymatchlist",
        level: 4,
        title: "/identity/match/list",
      },
{
        id: "identitydocumentsuploadsget",
        level: 4,
        title: "/identity/documents/uploads/get",
      },
{
        id: "webhooks-beta",
        level: 3,
        title: "Webhooks (beta)",
      },
{
        id: "default_update",
        level: 4,
        title: "DEFAULT_UPDATE",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
